import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Chip, Button
} from '@mui/material';
import { ShoppingCart, RequestQuote, Chat, LocalShipping } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const CustomerDashboard = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.auth);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const { data } = await api.get('/customers/dashboard/stats');
        setStats(data.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadStats();
  }, []);

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  const cards = [
    { icon: <ShoppingCart sx={{ fontSize: 40, color: '#1565c0' }} />, label: t('my_orders'), value: stats?.totalOrders || 0, link: '/customer/orders' },
    { icon: <RequestQuote sx={{ fontSize: 40, color: '#e65100' }} />, label: t('my_quotes'), value: stats?.activeQuotes || 0, link: '/customer/quotes' },
    { icon: <Chat sx={{ fontSize: 40, color: '#6a1b9a' }} />, label: t('messages'), value: stats?.unreadMessages || 0, link: '/customer/chat' },
    { icon: <LocalShipping sx={{ fontSize: 40, color: '#2e7d32' }} />, label: 'Active Shipments', value: stats?.activeShipments || 0, link: '/customer/orders' },
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Welcome, {user?.firstName}!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Customer ID: <Chip label={user?.customerId} size="small" color="primary" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Your business gateway to global industrial products.
      </Typography>

      <Grid container spacing={3}>
        {cards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card component={Link} to={card.link} sx={{
              textDecoration: 'none',
              '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 4 }
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {card.icon}
                <Box>
                  <Typography variant="h4" fontWeight={700}>{card.value}</Typography>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Quick Actions</Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="contained" component={Link} to="/products">Browse Products</Button>
          <Button variant="outlined" component={Link} to="/customer/quotes">Request Quote</Button>
          <Button variant="outlined" component={Link} to="/customer/chat">Contact Support</Button>
        </Box>
      </Card>

      {/* Recent Orders */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Recent Orders</Typography>
          {stats?.recentOrders?.length > 0 ? stats.recentOrders.map((order, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #eee' }}>
              <Typography variant="body2" fontWeight={600}>{order.orderNumber}</Typography>
              <Typography variant="body2">${order.totalAmount?.toLocaleString()}</Typography>
              <Chip label={order.status} size="small" />
              <Typography variant="caption">{new Date(order.createdAt).toLocaleDateString()}</Typography>
            </Box>
          )) : <Typography variant="body2" color="text.secondary">No orders yet. Start browsing products!</Typography>}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerDashboard;
